// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/connectors/ConnectorGovernancePanel.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConnectorGovernanceTable from "./ConnectorGovernanceTable";
import ConnectorDetailDrawer from "./ConnectorDetailDrawer";
import ConnectorAuthorizationHistory from "./ConnectorAuthorizationHistory";
import ConnectorActionModal from "./ConnectorActionModal";
import ConnectorSetupWizard from "./setup/ConnectorSetupWizard";

function statusLabel(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function refreshedLabel(value) {
  if (!value) return "Latest connector state loaded.";

  try {
    const refreshed = new Date(value);
    return `Refreshed ${refreshed.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    })}`;
  } catch {
    return "Refreshed just now";
  }
}

function MetricCard({ label, value, tone }) {
  return (
    <article
      className={`enterprise-admin-metric-card enterprise-admin-connector-metric ${
        tone || ""
      }`}
    >
      <span>{label}</span>
      <strong>{value ?? 0}</strong>
    </article>
  );
}

function modalConnector(connector) {
  if (!connector) return null;

  return {
    ...connector,
    owner_user_id: connector?.owner?.user_id || connector.owner_user_id || null,
    owner_name: connector?.owner?.name || connector.owner_name || null,
    owner_email: connector?.owner?.email || connector.owner_email || null,
  };
}

export default function ConnectorGovernancePanel({
  connectors = [],
  summary = {},
  audit = [],
  users = [],
  actions = {},
  saving = false,
  onRefresh,
  refreshedAt,
  captureApiBaseUrl,
  captureGetAccessToken,
  captureTenantId,
  captureUserId,
  initialSetupProviderKey = null,
  setupRequested = false,
  returnTo = null,
  returnStep = "connectors",
  onReturn,
}) {
  const [selectedConnectorId, setSelectedConnectorId] = useState(
    connectors[0]?.connector_id || null
  );
  const [modalAction, setModalAction] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupCatalog, setSetupCatalog] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState(null);
  const [setupProviderKey, setSetupProviderKey] = useState(null);
  const [healthByConnector, setHealthByConnector] = useState({});
  const [healthLoadingId, setHealthLoadingId] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [setupReconnectMode, setSetupReconnectMode] = useState(false);
  const autoOpenedSetupRef = useRef(false);

  useEffect(() => {
    if (!connectors.length) {
      setSelectedConnectorId(null);
      return;
    }

    const stillExists = connectors.some(
      (connector) => connector.connector_id === selectedConnectorId
    );

    if (!stillExists) {
      setSelectedConnectorId(connectors[0].connector_id);
    }
  }, [connectors, selectedConnectorId]);

  const selectedConnector = useMemo(
    () =>
      connectors.find(
        (connector) => connector.connector_id === selectedConnectorId
      ) ||
      connectors[0] ||
      null,
    [connectors, selectedConnectorId]
  );

  const selectedHealth = selectedConnector
    ? healthByConnector[selectedConnector.connector_id] || null
    : null;

  const metrics = summary || {};
  const totalCount = metrics.total_connectors ?? connectors.length;
  const connectedCount =
    metrics.connected ??
    connectors.filter((connector) =>
      ["connected", "healthy", "degraded", "paused"].includes(
        connector.lifecycle_state
      )
    ).length;
  const healthyCount =
    metrics.healthy ??
    connectors.filter(
      (connector) => connector.lifecycle_state === "healthy"
    ).length;
  const attentionCount =
    metrics.needs_attention ??
    connectors.filter((connector) =>
      ["degraded", "reauthorization_required"].includes(
        connector.lifecycle_state
      )
    ).length;

  const loadSetupCatalog = useCallback(async () => {
    if (setupCatalog) return setupCatalog;

    const catalog = await actions.getConnectorSetupCatalog?.();
    const operationByProvider = new Map(
      connectors.map((connector) => [connector.provider_key, connector])
    );
    const enrichedCatalog = {
      ...(catalog || {}),
      items: (catalog?.items || []).map((item) => {
        const operation = operationByProvider.get(item.provider_key);
        return operation
          ? {
              ...item,
              lifecycle_state: operation.lifecycle_state,
              status_label: operation.status_label,
              health_status: operation.health_status,
              connector_id: operation.connector_id,
              verified_account: operation.verified_account,
            }
          : item;
      }),
    };
    setSetupCatalog(enrichedCatalog);
    return enrichedCatalog;
  }, [actions, connectors, setupCatalog]);

  const openSetup = useCallback(async (providerKey = null, options = {}) => {
    setSetupOpen(true);
    setSetupProviderKey(providerKey);
    setSetupReconnectMode(options.reconnectMode === true);
    setSetupError(null);
    setSetupLoading(true);

    try {
      await loadSetupCatalog();
    } catch (err) {
      setSetupError(String(err?.message || err));
    } finally {
      setSetupLoading(false);
    }
  }, [loadSetupCatalog]);

  const closeSetup = () => {
    setSetupOpen(false);
    setSetupProviderKey(null);
    setSetupReconnectMode(false);
    setSetupError(null);

    if (returnTo === "mission-launch" && typeof onReturn === "function") {
      onReturn("mission-launch", {
        step: returnStep || "connectors",
        connectorRefresh: String(Date.now()),
      });
    }
  };

  useEffect(() => {
    if (
      !setupRequested ||
      !initialSetupProviderKey ||
      autoOpenedSetupRef.current ||
      setupLoading
    ) {
      return;
    }

    autoOpenedSetupRef.current = true;
    const operation = connectors.find(
      (connector) => connector.provider_key === initialSetupProviderKey
    );
    const reconnectMode = ["disconnected", "reauthorization_required"].includes(
      String(operation?.lifecycle_state || "").toLowerCase()
    );

    openSetup(initialSetupProviderKey, {
      reconnectMode,
    });
  }, [
    connectors,
    initialSetupProviderKey,
    openSetup,
    setupLoading,
    setupRequested,
  ]);

  const refreshSelectedDetail = async () => {
    if (!selectedConnector?.connector_id) return null;
    const result = await actions.getConnectorDetail?.(
      selectedConnector.connector_id
    );
    await onRefresh?.();
    return result;
  };

  const runHealth = async () => {
    if (!selectedConnector?.connector_id) return;

    setLocalError(null);
    setHealthLoadingId(selectedConnector.connector_id);

    try {
      const result = await actions.getConnectorHealth?.(
        selectedConnector.connector_id
      );
      setHealthByConnector((current) => ({
        ...current,
        [selectedConnector.connector_id]: result,
      }));
      await onRefresh?.();
    } catch (err) {
      setLocalError(String(err?.message || err));
    } finally {
      setHealthLoadingId(null);
    }
  };

  const beginReconnect = async () => {
    if (!selectedConnector?.connector_id) return;

    setLocalError(null);
    try {
      await actions.reconnectConnector?.(selectedConnector.connector_id, {
        reason: "Reconnect initiated from Enterprise Administration",
      });
      await onRefresh?.();
      await openSetup(selectedConnector.provider_key, {
        reconnectMode: true,
      });
    } catch (err) {
      setLocalError(String(err?.message || err));
    }
  };

  const handleAction = async (action, payload = {}) => {
    if (!selectedConnector?.connector_id) return;
    const connectorId = selectedConnector.connector_id;

    setLocalError(null);

    try {
      if (action === "reauthorize") {
        await actions.requestConnectorReauthorization?.(
          connectorId,
          payload.reason
        );
      } else if (action === "pause") {
        await actions.pauseConnector?.(connectorId, payload.reason);
      } else if (action === "resume") {
        await actions.resumeConnector?.(connectorId, payload.reason);
      } else if (action === "owner") {
        await actions.markConnectorOwner?.(connectorId, payload);
      } else if (action === "governance") {
        await actions.updateConnectorGovernance?.(connectorId, payload);
      } else if (action === "reverify") {
        const result = await actions.reverifyConnector?.(
          connectorId,
          payload
        );
        setHealthByConnector((current) => ({
          ...current,
          [connectorId]: result,
        }));
      } else if (action === "disconnect") {
        const result = await actions.disconnectConnector?.(
          connectorId,
          payload
        );
        setHealthByConnector((current) => ({
          ...current,
          [connectorId]: {
            status: "disconnected",
            healthy: false,
            provider_contacted: false,
            ...(result || {}),
          },
        }));
      }

      setModalAction(null);
      await refreshSelectedDetail();
    } catch (err) {
      setLocalError(String(err?.message || err));
    }
  };

  return (
    <section className="enterprise-admin-section-panel enterprise-admin-connectors-panel">
      <div className="enterprise-admin-section-header enterprise-admin-connector-header">
        <div>
          <p className="enterprise-admin-eyebrow">Connector Operations</p>
          <h2>Live connection control</h2>
          <p>
            Manage one governed Enterprise connection per provider, with
            customer-safe status, health, ownership, and recovery actions.
          </p>
        </div>

        <div className="enterprise-admin-connector-header-actions">
          <button
            type="button"
            className="enterprise-admin-add-connector-button"
            onClick={() => openSetup()}
            disabled={saving || setupLoading}
          >
            + Add connector
          </button>

          <div className="enterprise-admin-connector-refresh-panel">
            <button
              type="button"
              className="enterprise-admin-connector-refresh-button"
              onClick={onRefresh}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span
                    className="enterprise-admin-refresh-spinner"
                    aria-hidden="true"
                  />
                  Refreshing…
                </>
              ) : (
                "Refresh connectors"
              )}
            </button>
            <small>
              {saving
                ? "Checking connector state…"
                : refreshedLabel(refreshedAt)}
            </small>
          </div>
        </div>
      </div>

      {localError ? (
        <div className="enterprise-admin-error compact">
          <strong>Connector action failed</strong>
          <p>{localError}</p>
        </div>
      ) : null}

      <div className="enterprise-admin-metric-grid enterprise-admin-compact-metrics enterprise-admin-connector-metrics">
        <MetricCard label="Providers" value={totalCount} />
        <MetricCard label="Connected" value={connectedCount} />
        <MetricCard label="Healthy" value={healthyCount} />
        <MetricCard
          label="Needs attention"
          value={attentionCount}
          tone={attentionCount ? "warning" : ""}
        />
      </div>

      <div className="enterprise-admin-two-column enterprise-admin-connector-layout">
        <div className="enterprise-admin-card-stack enterprise-admin-connector-main-stack">
          <ConnectorGovernanceTable
            connectors={connectors}
            selectedConnectorId={selectedConnector?.connector_id}
            onSelect={(connector) =>
              setSelectedConnectorId(connector.connector_id)
            }
          />

          <ConnectorAuthorizationHistory
            events={audit}
            connectorId={selectedConnector?.connector_id || null}
          />
        </div>

        <div
          className="enterprise-admin-connector-detail-column"
          style={{ alignSelf: "start", minWidth: 0 }}
        >
          <ConnectorDetailDrawer
            captureApiBaseUrl={captureApiBaseUrl}
            captureGetAccessToken={captureGetAccessToken}
            captureTenantId={captureTenantId}
            captureUserId={captureUserId}
            connector={selectedConnector}
            health={selectedHealth}
            healthLoading={
              healthLoadingId === selectedConnector?.connector_id
            }
            saving={saving}
            statusLabel={statusLabel}
            onHealth={runHealth}
            onReverify={() => setModalAction("reverify")}
            onDisconnect={() => setModalAction("disconnect")}
            onReconnect={beginReconnect}
            onPause={() => setModalAction("pause")}
            onResume={() => setModalAction("resume")}
            onOwner={() => setModalAction("owner")}
            onGovernance={() => setModalAction("governance")}
          />
        </div>
      </div>

      {modalAction ? (
        <ConnectorActionModal
          action={modalAction}
          connector={modalConnector(selectedConnector)}
          users={users}
          saving={saving}
          onClose={() => setModalAction(null)}
          onSubmit={handleAction}
        />
      ) : null}

      {setupOpen ? (
        <ConnectorSetupWizard
          catalog={setupCatalog}
          loading={setupLoading}
          error={setupError}
          users={users}
          actions={actions}
          saving={saving}
          initialProviderKey={setupProviderKey}
          reconnectMode={setupReconnectMode}
          returnTo={returnTo}
          returnStep={returnStep}
          onRefresh={onRefresh}
          onClose={closeSetup}
        />
      ) : null}
    </section>
  );
}
